// ─────────────────────────────────────────────
// Oike System Design (Fase 7) — modules, steps and constants.
// Shared by the panel (src/components/diagnostic/SystemDesign.jsx) and the
// server (netlify/functions/shared/systemDesign*.ts). The methodology is in
// docs/diagnostic/system-design.md. Numbers are computed by code; the AI only
// writes; Ale validates. A step is stored in Diagnostic Outputs › Prompt.
// ─────────────────────────────────────────────

/**
 * The 7 modules. `dimension` is the key of the Diagnostic dimension it builds
 * (DIMENSIONS in diagnosticConfig.js); `words` helps to match a P4 priority to a module.
 * Modules 1 and 2 are the base: with their dimension at level 1-2 they always go in.
 */
export const SD_MODULES = [
  { key: 'icp',       number: 1, dimension: 'icp',               step: 'sd_icp',       label: 'ICP',               title: 'Ficha de ICP',
    base: true, words: ['icp', 'cliente ideal', 'mercado', 'segmento'] },
  { key: 'process',   number: 2, dimension: 'process',           step: 'sd_process',   label: 'Proceso',           title: 'Mapa del proceso y criterios de calificación',
    base: true, words: ['proceso', 'funnel', 'embudo', 'etapas', 'calificaci', 'criterios'] },
  { key: 'pipeline',  number: 3, dimension: 'pipelineGeneration', step: 'sd_pipeline',  label: 'Pipeline',          title: 'Cadencias y metas de actividad',
    words: ['pipeline', 'prospecci', 'canales', 'cadencia', 'generaci'] },
  { key: 'messaging', number: 4, dimension: 'messaging',         step: 'sd_messaging', label: 'Messaging',         title: 'Biblioteca de mensajes y objeciones',
    words: ['mensaje', 'messaging', 'objecion', 'pitch'] },
  { key: 'crmData',   number: 5, dimension: 'toolsDataAI',       step: 'sd_crmData',   label: 'CRM y datos',       title: 'Guía de configuración del CRM y política de IA',
    words: ['herramienta', 'crm', 'datos', 'licencia', ' ia ', 'inteligencia artificial'] },
  { key: 'ritual',    number: 6, dimension: 'metricsManagement', step: 'sd_ritual',    label: 'Ritual y métricas', title: 'Tablero de KPIs y agenda de la reunión semanal',
    words: ['metrica', 'kpi', 'gestion', 'reunion', 'ritual', 'meta'] },
  { key: 'accounts',  number: 7, dimension: 'accountStrategy',   step: 'sd_accounts',  label: 'Cuentas',           title: 'Plan de cuenta',
    words: ['cuentas', 'cuenta', 'preparaci', 'stakeholder', 'estrategia de cuentas'] },
];
export const SD_MODULE_KEYS = SD_MODULES.map(m => m.key);
export const SD_MODULE_STEPS = SD_MODULES.map(m => m.step);
export const SD_PLAYBOOK_STEP = 'sd_playbook';
export const SD_SCORE_STEP = 'sd_score';
/** Every Prompt value System Design writes in Diagnostic Outputs. */
export const SD_ALL_STEPS = [...SD_MODULE_STEPS, SD_PLAYBOOK_STEP, SD_SCORE_STEP];
/** Steps the AI runs (the playbook is assembled by code). */
export const SD_AI_STEPS = [...SD_MODULE_STEPS, SD_SCORE_STEP];
export const isSdStep = (step) => SD_ALL_STEPS.includes(step);
export const moduleOfStep = (step) => SD_MODULES.find(m => m.step === step) || null;
export const moduleOfKey = (key) => SD_MODULES.find(m => m.key === key) || null;

/** "Un System Design trabaja 3 o 4 módulos". More is allowed (the panel warns). */
export const SD_RECOMMENDED_MODULES = { min: 3, max: 4 };

// ── Numbers ──

/** An industry or size segment needs this many closed deals (won + lost) to be ranked as "the type of client that wins most". */
export const SD_SEGMENT_MIN_CLOSED = 3;
/** A funnel stage needs this many deals in its base to be called the place where it falls. */
export const SD_STAGE_MIN_BASE = 5;
/** Days after the first touch for each step of a cadence: the only day numbers the AI may write (besides "día N" forms). */
export const SD_CADENCE_DAYS = [1, 3, 5, 8, 12, 15];
/** The weekly meeting lasts 45 minutes (docs/diagnostic/system-design.md); the blocks are the only minute numbers the AI may write. */
export const SD_AGENDA_MINUTES = 45;
export const SD_AGENDA_BLOCKS = [5, 10, 15, 20];
/** Weeks in a month, to turn a monthly goal into a weekly one. */
export const SD_WEEKS_PER_MONTH = 4.33;
export const SD_GOAL_LIMITS = { amountMax: 1e12, monthsMax: 60 };
export const SD_NOTE_MAX = 3000;

/** Qualification frameworks (Oike does not impose one: the sale type chooses). */
export const SD_FRAMEWORKS = {
  transactional: { key: 'bant', name: 'BANT adaptado', why: 'venta de ticket chico y ciclo corto' },
  complex: { key: 'meddpicc', name: 'MEDDPICC simplificado', why: 'venta compleja, con varios stakeholders' },
};

/** The text of a missing datum: the AI copies it where something is missing. */
export const SD_MISSING = 'Falta: preguntar en el taller';

// ── Medición final ──

/** Level 3 (Definido) is the goal of every worked dimension (system-design.md, "Salida"). */
export const SD_TARGET_LEVEL = 3;
export const SD_SCORE_SOURCES = [
  { key: 'module', label: { es: 'Módulo aprobado' } },
  { key: 'ale_notes', label: { es: 'Notas de Ale' } },
];

// ── Preselection (Parte D) ──

const norm = (s) => ` ${String(s ?? '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, ' ').trim()} `;

/**
 * Which modules to suggest, from the validated levels of P4 and its priorities.
 * levels: { dimensionKey: 1-4 | null }; priorities: [{ title, what, why, service }].
 * 1. ICP and Proceso always, when their dimension is at level 1-2.
 * 2. The modules of the priorities that System Design covers, in the order of the priorities.
 * 3. Up to 4 with the lowest levels (1-2); at least 3 when there are enough.
 * Only a suggestion: Ale confirms. Each one says why (base | priority | lowest).
 */
export function suggestModules(levels, priorities) {
  const picked = [];
  const add = (m, why) => { if (m && !picked.some(p => p.key === m.key)) picked.push({ key: m.key, why }); };
  const low = (m) => [1, 2].includes(levels?.[m.dimension]);
  for (const m of SD_MODULES.filter(x => x.base && low(x))) add(m, 'base');
  for (const p of priorities || []) {
    if (p?.service && p.service !== 'systemDesign') continue;
    const text = norm(`${p.title} ${p.what}`);
    // The module whose words match the most; ties go to the lowest level
    // A word with spaces around it is matched whole (" ia "); any other is a stem ("prospecci")
    const needle = (w) => (/^\s|\s$/.test(w) ? norm(w) : norm(w).trim());
    const hits = SD_MODULES.map(m => ({ m, n: m.words.filter(w => text.includes(needle(w))).length })).filter(x => x.n > 0);
    hits.sort((a, b) => b.n - a.n || (levels?.[a.m.dimension] ?? 5) - (levels?.[b.m.dimension] ?? 5) || a.m.number - b.m.number);
    if (hits[0]) add(hits[0].m, 'priority');
  }
  const byLevel = SD_MODULES.filter(m => !picked.some(p => p.key === m.key) && levels?.[m.dimension] !== undefined && levels?.[m.dimension] !== null)
    .sort((a, b) => levels[a.dimension] - levels[b.dimension] || a.number - b.number);
  for (const m of byLevel) { if (picked.length >= SD_RECOMMENDED_MODULES.max) break; if (low(m)) add(m, 'lowest'); }
  for (const m of byLevel) { if (picked.length >= SD_RECOMMENDED_MODULES.min) break; add(m, 'lowest'); }
  return picked.slice(0, SD_RECOMMENDED_MODULES.max).sort((a, b) => moduleOfKey(a.key).number - moduleOfKey(b.key).number);
}

// ── Settings (Diagnostics › System Design, JSON written only by code) ──

/**
 * The System Design settings of a diagnostic, only known keys:
 * { modules: [key], confirmedBy, confirmedAt, goal: { amount, months } | null,
 *   stageMap: { meeting, opportunity } | null, contactToMeetingRate: 0-1 | null, notes: { dimensionKey: text } }
 */
export function parseSystemDesign(raw) {
  let v = raw;
  if (typeof raw === 'string') { try { v = JSON.parse(raw); } catch { v = {}; } }
  if (!v || typeof v !== 'object' || Array.isArray(v)) v = {};
  const finite = (n) => typeof n === 'number' && Number.isFinite(n);
  const dims = SD_MODULES.map(m => m.dimension);
  const goal = v.goal && finite(v.goal.amount) && v.goal.amount > 0 && v.goal.amount <= SD_GOAL_LIMITS.amountMax
    && Number.isInteger(v.goal.months) && v.goal.months >= 1 && v.goal.months <= SD_GOAL_LIMITS.monthsMax
    ? { amount: v.goal.amount, months: v.goal.months } : null;
  const str = (x) => (typeof x === 'string' && x.length <= 120 ? x : null);
  const stageMap = v.stageMap && str(v.stageMap.meeting) && str(v.stageMap.opportunity) ? { meeting: v.stageMap.meeting, opportunity: v.stageMap.opportunity } : null;
  const rate = finite(v.contactToMeetingRate) && v.contactToMeetingRate > 0 && v.contactToMeetingRate <= 1 ? v.contactToMeetingRate : null;
  const notes = {};
  if (v.notes && typeof v.notes === 'object') for (const d of dims) if (typeof v.notes[d] === 'string' && v.notes[d].trim()) notes[d] = v.notes[d].slice(0, SD_NOTE_MAX);
  return {
    modules: Array.isArray(v.modules) ? SD_MODULE_KEYS.filter(k => v.modules.includes(k)) : [],
    confirmedBy: typeof v.confirmedBy === 'string' ? v.confirmedBy : null,
    confirmedAt: typeof v.confirmedAt === 'string' ? v.confirmedAt : null,
    goal, stageMap, contactToMeetingRate: rate, notes,
  };
}
